import React from 'react';
import PropTypes from 'prop-types';
import Dialog, {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from 'material-ui/Dialog';
import Button from 'material-ui/Button';
import { RESTAPI_URI } from '../../../config';

const ACCESS_LABELS = {
  request: 'Request VDR Access',
  pending: 'VDR Access Pending',
  granted: 'Access VDR',
};

const ACCESS_STATES = {
  NONE: 'none',
  USER_SIGNED: 'user_signed',
  REQUESTED: 'requested',
  INVITED: 'invited',
  GRANTED: 'granted',
  REVOKED_PENDING: 'revoked_pending',
  REJECTED: 'rejected',
  REVOKED: 'revoked',
  AGREEMENT_OUTDATED: 'agreement_outdated',
  ERROR: 'error',
};

const getStatusFromValue = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().toLowerCase();
};

const findStatus = (source) => {
  if (!source || typeof source !== 'object') {
    return '';
  }

  const directAccessStatus = getStatusFromValue(source.accessStatus);
  if (directAccessStatus) {
    return directAccessStatus;
  }

  const directStatus = getStatusFromValue(source.status);
  if (directStatus) {
    return directStatus;
  }

  const directSignatureStatus = getStatusFromValue(source.signatureStatus);
  if (directSignatureStatus) {
    return directSignatureStatus;
  }

  if (source.data && typeof source.data === 'object') {
    const dataStatus = findStatus(source.data);
    if (dataStatus) {
      return dataStatus;
    }
  }

  if (source.signatureRequest && typeof source.signatureRequest === 'object') {
    const requestStatus = findStatus(source.signatureRequest);
    if (requestStatus) {
      return requestStatus;
    }
  }

  if (source.item && typeof source.item === 'object') {
    const itemStatus = findStatus(source.item);
    if (itemStatus) {
      return itemStatus;
    }
  }

  return '';
};

const getAuthToken = () => localStorage.getItem('authToken');

const getRequestHeaders = (extraHeaders = {}) => {
  const authToken = getAuthToken();
  const authorizationValue = authToken
    ? authToken.startsWith('Bearer ')
      ? authToken
      : `Bearer ${authToken}`
    : null;

  return authorizationValue
    ? {
      Authorization: authorizationValue,
      ...extraHeaders,
    }
    : {
      ...extraHeaders,
    };
};

const normalizeAccessStatus = (status) => {
  const normalized = getStatusFromValue(status);

  if (
    normalized === ACCESS_STATES.NONE ||
    normalized === ACCESS_STATES.USER_SIGNED ||
    normalized === ACCESS_STATES.REQUESTED ||
    normalized === ACCESS_STATES.INVITED ||
    normalized === ACCESS_STATES.GRANTED ||
    normalized === ACCESS_STATES.REVOKED_PENDING ||
    normalized === ACCESS_STATES.REJECTED ||
    normalized === ACCESS_STATES.REVOKED ||
    normalized === ACCESS_STATES.AGREEMENT_OUTDATED ||
    normalized === ACCESS_STATES.ERROR
  ) {
    return normalized;
  }

  return ACCESS_STATES.NONE;
};

const mapToSimpleStatus = (status) => {
  const normalized = normalizeAccessStatus(status);

  if (normalized === ACCESS_STATES.GRANTED) {
    return 'granted';
  }

  if (normalized === ACCESS_STATES.REQUESTED || normalized === ACCESS_STATES.INVITED) {
    return 'pending';
  }

  return 'request';
};

class JoinVdrButtonBase extends React.Component {
  state = {
    dataroomURL: '',
    hasDataroomConfigured: false,
    accessStatus: ACCESS_STATES.NONE,
    loading: true,
    isModalOpen: false,
    requestingAccess: false,
    refreshingAccessStatus: false,
  };

  componentDidMount() {
    this.loadStatuses();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.offerId !== this.props.offerId) {
      this.loadStatuses();
    }
  }

  get modalConfig() {
    const simpleStatus = mapToSimpleStatus(this.state.accessStatus);

    if (simpleStatus === 'granted') {
      return {
        title: 'Data Room Access',
        body: 'Your access is ready. You can open the data room.',
        primaryAction: 'open_dataroom',
        primaryLabel: 'Access VDR',
      };
    }

    if (simpleStatus === 'pending') {
      return {
        title: 'Request Pending',
        body: 'Your request is being reviewed.',
        primaryAction: null,
        primaryLabel: null,
      };
    }

    return {
      title: 'Request Data Room Access',
      body: 'Request access to the data room.',
      primaryAction: 'request_access',
      primaryLabel: 'Request VDR Access',
    };
  }

  get buttonLabel() {
    const simpleStatus = mapToSimpleStatus(this.state.accessStatus);
    return ACCESS_LABELS[simpleStatus] || ACCESS_LABELS.request;
  }

  get isButtonDisabled() {
    const { disabled } = this.props;
    const { loading } = this.state;

    return disabled || loading;
  }

  fetchHasDataroomConfigured = async () => {
    const { offerId } = this.props;

    try {
      let res = await fetch(
        `${RESTAPI_URI}/hellosign/offer-template/${offerId}/dataroom`,
        {
          method: 'GET',
          headers: getRequestHeaders(),
        }
      );

      if (res.ok) {
        const json = await res.json().catch(() => ({}));
        const hasDataroom = Boolean(json.hasDataroom);
        const hasDataroomUrl = Boolean(json.dataroomURL);
        const hasItemDataroomUrl = Boolean(json.item && json.item.dataroomURL);
        return hasDataroom || hasDataroomUrl || hasItemDataroomUrl;
      }

      res = await fetch(`${RESTAPI_URI}/hellosign/offer-template/${offerId}`, {
        method: 'GET',
        headers: getRequestHeaders(),
      });

      if (res.ok) {
        const json = await res.json().catch(() => ({}));
        return Boolean(
          json.dataroomURL || (json.item && json.item.dataroomURL)
        );
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  fetchAccessStatus = async () => {
    const { offerId } = this.props;

    const accessRes = await fetch(
      `${RESTAPI_URI}/dataroom-access/investor/offer/${offerId}/status`,
      {
        method: 'GET',
        headers: getRequestHeaders(),
      }
    );

    if (!accessRes.ok) {
      return ACCESS_STATES.ERROR;
    }

    const accessBody = await accessRes.json().catch(() => ({}));
    return normalizeAccessStatus(findStatus(accessBody));
  };

  loadStatuses = async () => {
    this.setState({ loading: true });

    try {
      const [accessStatus, hasDataroomConfigured] = await Promise.all([
        this.fetchAccessStatus(),
        this.fetchHasDataroomConfigured(),
      ]);

      this.setState({
        accessStatus: normalizeAccessStatus(accessStatus),
        hasDataroomConfigured,
        loading: false,
      });
    } catch (error) {
      this.setState({
        accessStatus: ACCESS_STATES.ERROR,
        hasDataroomConfigured: false,
        loading: false,
      });
    }
  };

  handleModalClose = () => {
    this.setState({ isModalOpen: false });
  };

  handleButtonClick = async () => {
    this.setState({ isModalOpen: true });
  };

  fetchDataroomURL = async () => {
    const { offerId } = this.props;

    try {
      // Try primary endpoint
      let res = await fetch(
        `${RESTAPI_URI}/hellosign/offer-template/${offerId}/dataroom`,
        {
          method: 'GET',
          headers: getRequestHeaders(),
        }
      );

      if (res.ok) {
        const json = await res.json().catch(() => ({}));
        const url = (json.item && json.item.dataroomURL) || json.dataroomURL || '';
        return url;
      }

      // Try fallback endpoint
      res = await fetch(
        `${RESTAPI_URI}/hellosign/offer-template/${offerId}`,
        {
          method: 'GET',
          headers: getRequestHeaders(),
        }
      );

      if (res.ok) {
        const json = await res.json().catch(() => ({}));
        const url = (json.item && json.item.dataroomURL) || json.dataroomURL || '';
        return url;
      }

      return '';
    } catch (error) {
      console.error('Error fetching dataroom URL:', error);
      return '';
    }
  };

  handleOpenDataRoom = async () => {
    let { dataroomURL } = this.state;

    // If URL is not cached, fetch it first
    if (!dataroomURL) {
      dataroomURL = await this.fetchDataroomURL();
      if (dataroomURL) {
        this.setState({ dataroomURL });
      }
    }

    if (dataroomURL) {
      window.open(dataroomURL, '_blank', 'noopener,noreferrer');
    }
  };

  handleRequestAccess = async () => {
    const { offerId } = this.props;

    try {
      this.setState({ requestingAccess: true });

      const requestRes = await fetch(
        `${RESTAPI_URI}/dataroom-access/investor/offer/${offerId}/request`,
        {
          method: 'POST',
          headers: getRequestHeaders({
            'Content-Type': 'application/json',
          }),
        }
      );

      if (!requestRes.ok) {
        throw new Error('Failed to request data room access');
      }

      const requestBody = requestRes.ok ? await requestRes.json() : {};
      const nextStatus =
        normalizeAccessStatus(findStatus(requestBody)) || ACCESS_STATES.REQUESTED;

      this.setState({
        accessStatus: nextStatus,
      });

      this.setState({ refreshingAccessStatus: true });
      const refreshedStatus = await this.fetchAccessStatus();

      this.setState({
        accessStatus: normalizeAccessStatus(refreshedStatus || nextStatus),
        requestingAccess: false,
        refreshingAccessStatus: false,
      });
    } catch (error) {
      this.setState({
        accessStatus: ACCESS_STATES.ERROR,
        requestingAccess: false,
        refreshingAccessStatus: false,
      });
    }
  };

  handlePrimaryAction = async () => {
    const { primaryAction } = this.modalConfig;

    if (primaryAction === 'request_access') {
      await this.handleRequestAccess();
      return;
    }

    if (primaryAction === 'open_dataroom') {
      await this.handleOpenDataRoom();
    }
  };

  render() {
    const {
      isModalOpen,
      requestingAccess,
      refreshingAccessStatus,
    } = this.state;
    const { title, body, primaryAction, primaryLabel } = this.modalConfig;
    const isModalBusy = requestingAccess || refreshingAccessStatus;

    return (
      <React.Fragment>
        <Dialog open={isModalOpen} onClose={this.handleModalClose}>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{body}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.handleModalClose}
              className='action-button'
              disabled={isModalBusy}
            >
              Close
            </Button>
            {primaryAction && (
              <Button
                onClick={this.handlePrimaryAction}
                className='action-button'
                disabled={isModalBusy}
              >
                {isModalBusy ? 'Loading...' : primaryLabel}
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <button
          onClick={this.handleButtonClick}
          className='btn-small-outline w-button'
          style={{ width: 'inherit', marginRight: 10 }}
          disabled={this.isButtonDisabled}
        >
          {this.buttonLabel}
        </button>
      </React.Fragment>
    );
  }
}

const JoinVdrButton = (props) => <JoinVdrButtonBase {...props} />;

JoinVdrButton.propTypes = {
  offerId: PropTypes.string.isRequired,
  investorUser: PropTypes.object,
  tenant: PropTypes.object,
  disabled: PropTypes.bool,
  toggleDataRoom: PropTypes.bool,
};

JoinVdrButton.defaultProps = {
  investorUser: null,
  tenant: null,
  disabled: false,
  toggleDataRoom: false,
};

export default JoinVdrButton;



// WEBPACK FOOTER //
// ./src/components/OfferOverview/JoinVdrButton/index.js